import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarIcon,
  CloudArrowUpIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import {
  createProspectList,
  deleteProspectList,
  getProspectLists,
  processProspectListImport,
  ProspectListImportPreview,
  previewProspectListImport,
  updateProspectList,
  uploadProspectListFile,
} from '../../services/prospectListService';
import { ProspectList } from '../../types/prospectList';
import { showErrorToast, showSuccessToast } from '../../utils/toastUtils';

interface ProspectListManagementProps {
  isLoading?: boolean;
}

interface ProspectListFormState {
  name: string;
  description: string;
  sirets: string[];
}

interface ImportState {
  listId: number | null;
  file: File | null;
  preview?: ProspectListImportPreview;
  siretColumn?: string;
  step: 1 | 2 | 3;
  isUploading: boolean;
  isProcessing: boolean;
}

const DEFAULT_FORM_STATE: ProspectListFormState = {
  name: '',
  description: '',
  sirets: [],
};

const createDefaultImportState = (listId: number | null = null): ImportState => ({
  listId,
  file: null,
  preview: undefined,
  siretColumn: undefined,
  step: 1,
  isUploading: false,
  isProcessing: false,
});

const ProspectListManagement: React.FC<ProspectListManagementProps> = ({ isLoading = false }) => {
  const [prospectLists, setProspectLists] = useState<ProspectList[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState<number | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingList, setEditingList] = useState<ProspectList | null>(null);
  const [formState, setFormState] = useState<ProspectListFormState>(DEFAULT_FORM_STATE);
  const [formError, setFormError] = useState<string | null>(null);
  const [newSiretInput, setNewSiretInput] = useState('');
  const [importState, setImportState] = useState<ImportState>(createDefaultImportState());
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const isFormValid = useMemo(() => {
    return formState.name.trim().length > 0;
  }, [formState.name]);

  const loadProspectLists = async () => {
    setIsFetching(true);
    try {
      const lists = await getProspectLists();
      setProspectLists(lists);
    } catch (error) {
      console.error('Error fetching prospect lists', error);
      showErrorToast('Erreur lors du chargement des listes de prospects', 'fetch-prospect-lists-error');
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    loadProspectLists();
  }, []);

  const resetForm = () => {
    setEditingList(null);
    setFormState(DEFAULT_FORM_STATE);
    setFormError(null);
    setNewSiretInput('');
  };

  const handleOpenCreateDrawer = () => {
    resetForm();
    setIsDrawerOpen(true);
  };

  const handleEditList = (list: ProspectList) => {
    setEditingList(list);
    setFormState({
      name: list.name,
      description: list.description ?? '',
      sirets: [...list.sirets],
    });
    setIsDrawerOpen(true);
  };

  const handleAddSiret = () => {
    const cleaned = newSiretInput.replace(/\s+/g, '');

    if (!/^\d{14}$/.test(cleaned)) {
      setFormError('Veuillez saisir un SIRET valide (14 chiffres).');
      return;
    }

    if (formState.sirets.includes(cleaned)) {
      setFormError('Ce SIRET est déjà présent dans la liste.');
      return;
    }

    setFormState((prev) => ({
      ...prev,
      sirets: [cleaned, ...prev.sirets],
    }));
    setNewSiretInput('');
    setFormError(null);
  };

  const handleRemoveSiret = (siret: string) => {
    setFormState((prev) => ({
      ...prev,
      sirets: prev.sirets.filter((existing) => existing !== siret),
    }));
  };

  const formattedSirets = useMemo(() => {
    return formState.sirets.map((siret) => ({
      value: siret,
      formatted: siret.replace(/(\d{3})(?=\d)/g, '$1 '),
    }));
  }, [formState.sirets]);

  const handleSaveList = async () => {
    if (!isFormValid) {
      setFormError('Merci de saisir un nom pour votre liste.');
      return;
    }

    setIsSaving(true);

    try {
      if (editingList) {
        await updateProspectList(editingList.id, {
          name: formState.name.trim(),
          description: formState.description.trim() || undefined,
          sirets: formState.sirets,
        });
        showSuccessToast('Liste de prospects mise à jour', 'update-prospect-list-success');
      } else {
        await createProspectList({
          name: formState.name.trim(),
          description: formState.description.trim() || undefined,
          sirets: formState.sirets,
        });
        showSuccessToast('Liste de prospects créée', 'create-prospect-list-success');
      }

      setIsDrawerOpen(false);
      resetForm();
      await loadProspectLists();
    } catch (error) {
      console.error('Error saving prospect list', error);
      const apiError = error as { response?: { data?: { message?: string } } };
      const message =
        apiError?.response?.data?.message ||
        (editingList
          ? "Erreur lors de la mise à jour de la liste"
          : "Erreur lors de la création de la liste");
      setFormError(message);
      showErrorToast(message, 'save-prospect-list-error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteList = async (list: ProspectList) => {
    if (!window.confirm(`Supprimer la liste "${list.name}" ?`)) {
      return;
    }

    setIsDeleteLoading(list.id);
    try {
      await deleteProspectList(list.id);
      showSuccessToast('Liste de prospects supprimée', 'delete-prospect-list-success');
      await loadProspectLists();
    } catch (error) {
      console.error('Error deleting prospect list', error);
      showErrorToast('Erreur lors de la suppression de la liste de prospects', 'delete-prospect-list-error');
    } finally {
      setIsDeleteLoading(null);
    }
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    const file = event.target.files[0];
    if (!importState.listId) {
      showErrorToast(
        'Veuillez sélectionner une liste de prospects avant de choisir un fichier.',
        'prospect-import-no-list'
      );
      event.target.value = '';
      return;
    }

    setImportState((prev) => ({
      ...prev,
      file,
      step: 1,
      preview: undefined,
      siretColumn: undefined,
    }));
  };

  const handleUploadFile = async () => {
    if (!importState.listId || !importState.file) {
      return;
    }

    setImportState((prev) => ({ ...prev, isUploading: true }));
    try {
      await uploadProspectListFile(importState.listId, importState.file);
      const preview = await previewProspectListImport();
      setImportState((prev) => ({
        ...prev,
        preview,
        step: 2,
      }));
      showSuccessToast('Fichier importé. Veuillez sélectionner la colonne SIRET.', 'prospect-import-upload-success');
    } catch (error) {
      console.error('Error uploading prospect list import file', error);
      showErrorToast('Erreur lors du téléchargement du fichier', 'prospect-import-upload-error');
    } finally {
      setImportState((prev) => ({ ...prev, isUploading: false }));
    }
  };

  const handleProcessImport = async () => {
    if (!importState.siretColumn) {
      showErrorToast('Veuillez sélectionner la colonne contenant les SIRET', 'prospect-import-column-error');
      return;
    }

    setImportState((prev) => ({ ...prev, isProcessing: true }));
    try {
      const result = await processProspectListImport(importState.siretColumn);
      showSuccessToast(
        `${result.totalImported} SIRET importés avec succès`,
        'prospect-import-process-success'
      );
      setImportState(createDefaultImportState());
      await loadProspectLists();
      setIsImportModalOpen(false);
    } catch (error) {
      console.error('Error processing prospect list import', error);
      const apiError = error as { response?: { data?: { message?: string } } };
      const message = apiError?.response?.data?.message || 'Erreur lors du traitement du fichier';
      showErrorToast(message, 'prospect-import-process-error');
      setImportState((prev) => ({ ...prev, isProcessing: false }));
    }
  };

  const renderImportModal = () => {
    const preview = importState.preview;

    if (!isImportModalOpen) {
      return null;
    }

    return (
      <div
        className="fixed inset-0 z-[1003] flex items-center justify-center bg-gray-900/50 px-4"
        role="dialog"
        aria-modal="true"
      >
        <div className="relative w-full max-w-3xl rounded-lg bg-white shadow-xl">
          <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Importer des SIRET depuis un fichier</h3>
              <p className="mt-1 text-sm text-gray-500">
                Téléversez un fichier Excel/CSV puis sélectionnez la colonne contenant les SIRET.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsImportModalOpen(false);
                setImportState(createDefaultImportState());
              }}
              className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              aria-label="Fermer la fenêtre d'import"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-6">
            <div>
              <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
                Liste ciblée :{' '}
                <span className="font-semibold">
                  {prospectLists.find((list) => list.id === importState.listId)?.name ?? 'Liste inconnue'}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-4 rounded-md border border-dashed border-gray-300 bg-gray-50 p-4">
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Téléversement du fichier</p>
                  <p className="text-xs text-gray-500">
                    Formats supportés: Excel (.xlsx, .xls) ou CSV. Seule la colonne SIRET sera importée.
                  </p>
                </div>

                <label className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                  <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                  <span>{importState.file ? 'Changer de fichier' : 'Sélectionner un fichier'}</span>
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileSelected} />
                </label>
              </div>

              {importState.file && (
                <div className="flex items-center justify-between rounded-md bg-white px-3 py-2 text-xs text-gray-600">
                  <span className="truncate">{importState.file.name}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setImportState((prev) => ({
                        ...prev,
                        file: null,
                        preview: undefined,
                        siretColumn: undefined,
                      }))
                    }
                    className="text-red-500 hover:text-red-600"
                  >
                    Retirer
                  </button>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleUploadFile}
                  disabled={!importState.file || importState.isUploading || importState.preview !== undefined}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
                >
                  {importState.isUploading ? 'Téléversement...' : 'Téléverser et analyser'}
                </button>
              </div>
            </div>

            {preview && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-800">Prévisualisation</h4>
              <p className="text-xs text-gray-500 mt-1">
                Sélectionnez la colonne correspondant aux SIRET, puis validez l'import.
              </p>
            </div>

            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    {preview.headers.map((header, index) => (
                      <th
                        key={`${header}-${index}`}
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium text-gray-600"
                      >
                        {header || '<Colonne sans nom>'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {preview.sampleData.map((row, index) => (
                    <tr key={`row-${index}`}>
                      {row.map((cell, cellIndex) => (
                        <td key={`cell-${cellIndex}`} className="px-3 py-2 text-xs text-gray-700">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <label htmlFor="siretColumn" className="block text-sm font-medium text-gray-700">
                Colonne SIRET
              </label>
              <select
                id="siretColumn"
                value={importState.siretColumn || ''}
                onChange={(event) =>
                  setImportState((prev) => ({ ...prev, siretColumn: event.target.value }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">Sélectionner une colonne</option>
                {preview.headers.map((header, index) => (
                  <option key={`${header}-${index}`} value={header}>
                    {header || '<Colonne sans nom>'}
                  </option>
                ))}
              </select>
            </div>
          </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={() => {
                setIsImportModalOpen(false);
                setImportState(createDefaultImportState());
              }}
              className="text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Annuler
            </button>

            <button
              type="button"
              onClick={handleProcessImport}
              disabled={!preview || !importState.siretColumn || importState.isProcessing}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
            >
              {importState.isProcessing ? 'Import en cours...' : 'Importer la sélection'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Gestion des listes de prospects</h2>
          <p className="mt-1 text-sm text-gray-600">
            Créez, modifiez et importez des listes de SIRET provenant de logiciels de gestion tiers.
          </p>
        </div>

        <button
          type="button"
          onClick={handleOpenCreateDrawer}
          disabled={isLoading || isFetching}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nouvelle liste
        </button>
      </div>

      {isFetching ? (
        <div className="py-10 text-center text-sm text-gray-500">Chargement des listes...</div>
      ) : prospectLists.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <PlusIcon className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-sm font-medium text-gray-900">Aucune liste de prospects</h3>
          <p className="mt-2 text-sm text-gray-500">
            Importez des SIRET pour exclure rapidement vos prospects existants des recherches.
          </p>
          <button
            type="button"
            onClick={handleOpenCreateDrawer}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Créer une liste
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {prospectLists.map((list) => (
            <div key={list.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-200">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{list.name}</h3>
                      {list.description && (
                        <p className="mt-1 text-sm text-gray-600">{list.description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setImportState(createDefaultImportState(list.id));
                      setIsImportModalOpen(true);
                    }}
                    disabled={isImportModalOpen}
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md disabled:opacity-60"
                    title="Importer un fichier"
                    aria-label="Importer des SIRET depuis un fichier"
                  >
                    <CloudArrowUpIcon className="h-4 w-4 inline-block mr-1" />
                    <span className="text-xs">Importer</span>
                  </button>

                      <button
                        type="button"
                        onClick={() => handleEditList(list)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                      >
                        <PencilIcon className="h-4 w-4 inline-block mr-1" />
                        <span className="text-xs">Modifier</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteList(list)}
                        disabled={isDeleteLoading === list.id}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-60"
                      >
                        <TrashIcon className="h-4 w-4 inline-block mr-1" />
                        <span className="text-xs">Supprimer</span>
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center">
                      <PlusIcon className="h-4 w-4 mr-1 text-gray-400" />
                      {list.sirets.length} SIRET
                    </span>
                    <span className="inline-flex items-center">
                      <UserIcon className="h-4 w-4 mr-1 text-gray-400" />
                      Créé par {list?.creator ? `${list.creator.firstName} ${list.creator.lastName}` : 'Inconnu'}
                    </span>
                    <span className="inline-flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" />
                      {new Date(list.createdAt).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>

              </div>
            </div>
          ))}
        </div>
      )}

      {renderImportModal()}

      {/* Drawer for create/edit */}
      <div
        className={`fixed inset-0 flex items-center justify-end z-[1002] transition-opacity ${
          isDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!isDrawerOpen}
      >
        <div
          className="absolute inset-0 bg-gray-900/40"
          onClick={() => {
            setIsDrawerOpen(false);
            resetForm();
          }}
        ></div>

        <div className="relative w-full max-w-2xl bg-white shadow-xl h-full overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {editingList ? 'Modifier la liste de prospects' : 'Créer une nouvelle liste de prospects'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {editingList
                  ? 'Mettez à jour les informations ou les SIRET existants.'
                  : 'Ajoutez un nom, une description et importez vos SIRET.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsDrawerOpen(false);
                resetForm();
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Fermer
            </button>
          </div>

          <div className="p-6 space-y-6">
            {formError && (
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{formError}</div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">Nom de la liste *</label>
              <input
                type="text"
                value={formState.name}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, name: event.target.value }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Ex: Prospects CRM Mars 2025"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description (optionnelle)
              </label>
              <textarea
                rows={3}
                value={formState.description}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, description: event.target.value }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Précisez l'origine des données, la date d'extraction, etc."
              />
            </div>

            <div>
              <div className="rounded-md bg-blue-50 border border-blue-200 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CloudArrowUpIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">
                      Import de fichiers Excel/CSV
                    </h4>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        Vous pouvez ajouter des SIRET manuellement ci-dessous, mais pour importer des fichiers Excel ou CSV,
                        vous devez d'abord <strong>créer la liste</strong>, puis utiliser le bouton "Importer" dans la liste créée.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <label className="block text-sm font-medium text-gray-700">
                SIRET manuels
              </label>
              <p className="text-xs text-gray-500">
                Ajoutez ou collez des SIRET individuels (14 chiffres).
              </p>

              <div className="mt-2 flex flex-col md:flex-row md:items-center md:space-x-3 space-y-2 md:space-y-0">
                <input
                  type="text"
                  value={newSiretInput}
                  onChange={(event) => setNewSiretInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddSiret();
                    }
                  }}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Saisissez un SIRET et appuyez sur Ajouter"
                />
                <button
                  type="button"
                  onClick={handleAddSiret}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  Ajouter
                </button>
              </div>

              {formattedSirets.length > 0 && (
                <div className="mt-3 bg-gray-50 border border-gray-200 rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-800">SIRET ajoutés ({formattedSirets.length})</h4>
                    <button
                      type="button"
                      onClick={() => setFormState((prev) => ({ ...prev, sirets: [] }))}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Tout supprimer
                    </button>
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {formattedSirets.map((siret) => (
                      <div
                        key={siret.value}
                        className="flex items-center justify-between text-xs bg-white border border-gray-200 rounded px-3 py-1"
                      >
                        <span>{siret.formatted}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSiret(siret.value)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Supprimer
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setIsDrawerOpen(false);
                resetForm();
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSaveList}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-60"
            >
              {isSaving ? 'Enregistrement...' : editingList ? 'Sauvegarder' : 'Créer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProspectListManagement;

